import { t } from '../strings';
import { clockInZone } from '../zonedTime';

/**
 * An event's reminder (piece 13; decision 1081): one person's own, a lead time
 * and a channel. 🔒 Text is offered only while the text seam reports an active
 * adapter and the person carries a mobile number. 🔒 A reminder is due at the
 * event's current start less its lead, read at send time, so an event that
 * moves carries its reminder with it and the send for the old time never
 * happens. A repeating event takes one too: the engine expands the series'
 * occurrences inside the lead window and sends once for each (decision 1606).
 */
export const EVENT_REMINDER_CHANNELS = ['Email', 'Text'] as const;
export type EventReminderChannel = (typeof EVENT_REMINDER_CHANNELS)[number];

/** The lead times the Reminder row offers by name, in minutes: 15 minutes, 1 hour and 1 day. */
export const EVENT_REMINDER_PRESETS = [15, 60, 1440] as const;

/** The longest lead a typed value may name, in minutes: thirty days. */
export const EVENT_REMINDER_MAX_MINUTES = 43_200;

/** How often the engine looks for a due reminder, in milliseconds (piece 9; decision 1081). */
export const EVENT_REMINDER_TICK_MS = 60_000;

/** How many failed sends the engine makes for one start before it stamps the row handled and audits the failure (piece 9; decision 1081). */
export const EVENT_REMINDER_MAX_ATTEMPTS = 5;

export interface EventReminderDraft {
  leadMinutes: number;
  channel: EventReminderChannel;
}

/** Why Text is greyed: no active text adapter behind the seam, or no mobile number on the person. */
export type TextReminderRefusal = 'noTextService' | 'noMobile';

/** What the Reminder row may offer the viewer. */
export interface ReminderChannels {
  text: { offered: boolean; reason: TextReminderRefusal | null };
}

/** The viewer's reminder of one event beside what they may choose; `reminder` is null where none is set. */
export interface EventReminderAnswer {
  reminder: EventReminderDraft | null;
  channels: ReminderChannels;
}

/** The two facts Text waits on, answered as the row reads them. */
export function reminderChannels(textAdapterActive: boolean, hasMobile: boolean): ReminderChannels {
  if (!textAdapterActive) return { text: { offered: false, reason: 'noTextService' } };
  if (!hasMobile) return { text: { offered: false, reason: 'noMobile' } };
  return { text: { offered: true, reason: null } };
}

export type EventReminderProblem = 'leadWhole' | 'leadTooLong' | 'channel' | TextReminderRefusal;

/** What stops a reminder saving, or null; the modal and the server ask the same question. */
export function eventReminderProblem(reminder: EventReminderDraft | null, facts: { channels: ReminderChannels }): EventReminderProblem | null {
  if (reminder === null) return null;
  if (!Number.isInteger(reminder.leadMinutes) || reminder.leadMinutes < 1) return 'leadWhole';
  if (reminder.leadMinutes > EVENT_REMINDER_MAX_MINUTES) return 'leadTooLong';
  if (!(EVENT_REMINDER_CHANNELS as readonly string[]).includes(reminder.channel)) return 'channel';
  if (reminder.channel === 'Text' && !facts.channels.text.offered) return facts.channels.text.reason ?? 'noTextService';
  return null;
}

/** The instant a reminder is due: the event's start less the lead. */
export function reminderSendAt(startsAt: Date, leadMinutes: number): Date {
  return new Date(startsAt.getTime() - leadMinutes * 60_000);
}

/**
 * 🔒 Due when the send time has passed, the start is still ahead, the event is
 * not cancelled, and no send was made for this start (decision 1081).
 */
export function reminderDue(
  reminder: { leadMinutes: number; sentForStartsAt: Date | null },
  event: { startsAt: Date; cancelledAt: Date | null; recurrenceRule: string | null },
  now: Date,
): boolean {
  if (event.cancelledAt !== null || event.recurrenceRule !== null) return false;
  if (event.startsAt.getTime() <= now.getTime()) return false;
  if (reminder.sentForStartsAt !== null && reminder.sentForStartsAt.getTime() === event.startsAt.getTime()) return false;
  return reminderSendAt(event.startsAt, reminder.leadMinutes).getTime() <= now.getTime();
}

/**
 * 🔒 One occurrence of a series is due when its send time has passed, its
 * start is still ahead, and it is later than the last occurrence a send went
 * for (decision 1606). Occurrences are sent in start order, so the stamp only
 * moves forward: one occurrence is sent once and the next is never skipped.
 */
export function occurrenceReminderDue(
  reminder: { leadMinutes: number; sentForStartsAt: Date | null },
  occurrenceStartsAt: Date,
  now: Date,
): boolean {
  if (occurrenceStartsAt.getTime() <= now.getTime()) return false;
  if (reminder.sentForStartsAt !== null && occurrenceStartsAt.getTime() <= reminder.sentForStartsAt.getTime()) return false;
  return reminderSendAt(occurrenceStartsAt, reminder.leadMinutes).getTime() <= now.getTime();
}

/** The day and time an instant reads in the person's own zone, the zone named: Mon, Sep 28, 9:00 AM EDT. */
export function reminderWhen(startsAt: Date, zone: string): string {
  const day = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric' }).format(startsAt);
  return `${day}, ${clockInZone(startsAt, zone)}`;
}

/**
 * 🔒 A Text reminder is one line: the event's subject, its day and time in the
 * person's own zone, and its place where it has one (decision 1081).
 */
export function reminderLine(event: { subject: string; startsAt: Date; place: string | null }, zone: string): string {
  const where = event.place ? ` at ${event.place}` : '';
  return `Reminder: ${event.subject.trim()}, ${reminderWhen(event.startsAt, zone)}${where}.`;
}

/** Whether a lead is one the row names, so the row opens on the name rather than on Custom. */
export function isPresetLead(leadMinutes: number): boolean {
  return (EVENT_REMINDER_PRESETS as readonly number[]).includes(leadMinutes);
}

/** The words a reminder problem reads in, on the form and in the server's refusal alike. */
export const EVENT_REMINDER_PROBLEM_KEYS = {
  leadWhole: 'event.reminder.problem.leadWhole',
  leadTooLong: 'event.reminder.problem.leadTooLong',
  channel: 'event.reminder.problem.channel',
  noTextService: 'event.reminder.problem.noTextService',
  noMobile: 'event.reminder.problem.noMobile',
} as const satisfies Record<EventReminderProblem, string>;

/** The lead as the row and the card read it: 15 minutes before, 1 hour before, 1 day before, or 90 minutes before. */
export function reminderLeadKey(leadMinutes: number): 'event.reminder.lead15' | 'event.reminder.lead60' | 'event.reminder.lead1440' | null {
  if (leadMinutes === 15) return 'event.reminder.lead15';
  if (leadMinutes === 60) return 'event.reminder.lead60';
  if (leadMinutes === 1440) return 'event.reminder.lead1440';
  return null;
}

/** The reminder as the event's page reads it under the time: 15 minutes before, by email. */
export function reminderSummary(reminder: EventReminderDraft): string {
  const key = reminderLeadKey(reminder.leadMinutes);
  const lead = key ? t(key) : `${reminder.leadMinutes} ${t('event.reminder.minutesBefore')}`;
  return `${lead}, ${t(reminder.channel === 'Text' ? 'event.reminder.byText' : 'event.reminder.byEmail')}`;
}
